/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import {
  Ad,
  AdBreak,
  AdContentTimePosition,
  AdPlaybackPolicy,
  ImaSsaiPlaybackPolicyHandler,
  PlaybackAutoSeekMode,
  PlaybackFastForwardMode,
  PlaybackInterruptedMode,
  PlaybackRepeatedMode,
  PlaybackRewindMode,
  Player,
} from '@quickplay/player-interface';
import { Logger } from '@quickplay/foundation';
import { getAdBreaksBetween, getTotalAdvertsBreaksDuration } from '../../util/AdUtil';

enum ActivePolicy {
  IDLE = 'idle',
  AUTO_SEEK = 'autoseek',
}

// To standardize float value precision upto 3 digit.
const DECIMAL_PLACES = 6;

export class SsaiAdPlaybackPolicyHandler implements ImaSsaiPlaybackPolicyHandler {
  /**
   * The array of all ad breaks.
   * @private
   */
  private adBreaks: AdBreak[] | null = null;
  private readonly fastForwardMode: PlaybackFastForwardMode = PlaybackFastForwardMode.TRICK_FW_PLAY_ALL;
  private readonly rewindMode: PlaybackRewindMode = PlaybackRewindMode.TRICK_RW_PLAY_ALL;
  private readonly autoSeekMode: PlaybackAutoSeekMode = PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL;
  private readonly repeatMode: PlaybackRepeatedMode = PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY;
  private readonly interruptMode: PlaybackInterruptedMode = PlaybackInterruptedMode.INTERRUPT_RESUME;
  private watchedAdBreaks: AdBreak[] = [];

  /**
   * An array that contains a list of seek positions to look for.
   * @private
   */
  private autoSeekPositions: number[] = [];
  /**
   * A value indicating the type of policy being applied.
   * @private
   */
  private policyInPlace: ActivePolicy;

  constructor(
    private readonly player: Player,
    private readonly streamManager: any,
    adPlaybackPolicy: AdPlaybackPolicy,
    private readonly logger: Logger,
  ) {
    this.fastForwardMode = adPlaybackPolicy.fastForwardMode;
    this.rewindMode = adPlaybackPolicy.rewindMode;
    this.autoSeekMode = adPlaybackPolicy.autoSeekMode;
    this.repeatMode = adPlaybackPolicy.repeatMode;
    this.interruptMode = adPlaybackPolicy.interruptMode;

    this.logger.log(
      `[DAI POLICY] [FastForwardMode: ${this.fastForwardMode}] [RewindMode: ${this.rewindMode}] [AutoSeekMode: ${
        this.autoSeekMode
      }] [RepeatMode: ${this.repeatMode}] [InterruptMode: ${this.interruptMode}] [InitialPlaybackTime: ${
        player.playbackOptions && player.playbackOptions.initialPlaybackTime
          ? player.playbackOptions.initialPlaybackTime
          : 0
      }]`,
    );

    this.policyInPlace = ActivePolicy.IDLE;
  }

  setAdBreaks(adBreaks: AdBreak[]): void {
    this.adBreaks = adBreaks;
  }

  playbackInterrupted(): void {
    // no-op
  }

  applySeekPlaybackPolicy = (position: number, adBreaks: AdBreak[]): number => {
    let startPosition: number | undefined = undefined;

    /**
     * Seek Playback policy;
     * - Forward playback policy: If the player'currentTime is less than
     * seekPosition.
     *  - Rewind playback policy: If the player'currentTime is greater than
     * seekPosition.
     */
    if (this.player.currentTime < position) {
      const midrollAdBreaks = this.getMidrollAdBreaks(this.player.currentTime, position, adBreaks);
      startPosition = this.playbackForwardPolicy(midrollAdBreaks);
      if (startPosition !== undefined) {
        this.autoSeekPositions.push(position);
        return startPosition;
      }
    } else if (this.player.currentTime > position) {
      const midrollAdBreaks = this.getMidrollAdBreaks(position, this.player.currentTime, adBreaks);
      startPosition = this.playbackRewindPolicy(midrollAdBreaks);
      if (startPosition !== undefined) {
        this.autoSeekPositions.push(position);
        return startPosition;
      }
    }

    return position;
  };

  private getMidrollAdBreaks(start: number, end: number, adBreaks: AdBreak[]): AdBreak[] {
    const filteredAdBreaks = getAdBreaksBetween(start, end, adBreaks);
    return filteredAdBreaks.filter((adBreak: AdBreak) => adBreak.position === AdContentTimePosition.MIDROLL);
  }

  playbackForwardPolicy(midrollAdBreaks: AdBreak[]): number | undefined {
    if (midrollAdBreaks.length) {
      switch (this.fastForwardMode) {
        case PlaybackFastForwardMode.TRICK_FW_PLAY_FIRST: {
          const firstAdBreak = midrollAdBreaks[0];

          if (firstAdBreak && this.playbackRepeatedRule(firstAdBreak)) {
            return firstAdBreak.startTime;
          }

          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_PLAY_LAST: {
          const lastAdBreak = midrollAdBreaks[midrollAdBreaks.length - 1];

          if (lastAdBreak && this.playbackRepeatedRule(lastAdBreak)) {
            return lastAdBreak.startTime;
          }

          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_SKIP_ALL: {
          break;
        }
        case PlaybackFastForwardMode.TRICK_FW_PLAY_ALL: {
          const position = this.handleFastForwardPlayAll(midrollAdBreaks);
          if (position !== undefined) {
            return position;
          }
          break;
        }
      }
    }

    return undefined;
  }

  private handleFastForwardPlayAll(midrollAdBreaks: AdBreak[]): number | undefined {
    // const firstAdBreak = midrollAdBreaks.shift();
    let firstAdBreak: AdBreak | undefined;

    midrollAdBreaks.forEach((adBreak: AdBreak) => {
      if (this.playbackRepeatedRule(adBreak)) {
        if (firstAdBreak === undefined) {
          firstAdBreak = adBreak;
        } else {
          this.autoSeekPositions.push(adBreak.startTime);
        }
      }
    });
    return (firstAdBreak && firstAdBreak.startTime) ?? undefined;
  }
  setWatchedAdBreak(adBreak: AdBreak): void {
    // Check if the adbreak is already marked as watched to avoid adding up duplicate data in an array.
    if (this.repeatMode === PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY && this.isAdBreakPresent(adBreak)) {
      return;
    }

    this.watchedAdBreaks.push(adBreak);
  }

  private isAdBreakPresent(adBreak: AdBreak): boolean {
    return this.watchedAdBreaks.some(watchedAdBreak => watchedAdBreak.startTime === adBreak.startTime);
  }

  playbackRewindPolicy(midrollAdBreaks: AdBreak[]): number | undefined {
    if (midrollAdBreaks.length) {
      switch (this.rewindMode) {
        case PlaybackRewindMode.TRICK_RW_PLAY_FIRST: {
          const firstAdBreak = midrollAdBreaks[length - 1];
          if (firstAdBreak && this.playbackRepeatedRule(firstAdBreak)) {
            return firstAdBreak.startTime;
          }

          break;
        }
        case PlaybackRewindMode.TRICK_RW_PLAY_LAST: {
          const lastAdBreak = midrollAdBreaks[0];
          if (lastAdBreak && this.playbackRepeatedRule(lastAdBreak)) {
            return lastAdBreak.startTime;
          }

          break;
        }
        case PlaybackRewindMode.TRICK_RW_SKIP_ALL: {
          break;
        }
        case PlaybackRewindMode.TRICK_RW_PLAY_ALL: {
          const position = this.handleFastForwardPlayAll(midrollAdBreaks);
          if (position !== undefined) {
            return position;
          }
          break;
        }
      }
    }
    return undefined;
  }

  playbackRepeatedRule = (adbreak: AdBreak): boolean => {
    if (this.watchedAdBreaks.length === 0) {
      return true;
    }
    switch (this.repeatMode) {
      case PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY: {
        return true;
      }
      case PlaybackRepeatedMode.REPEAT_PLAY_ONCE: {
        if (this.isAdBreakPresent(adbreak)) {
          return false;
        }
      }
    }
    return true;
  };

  resolveSeekIntentForInitialPlayback = (position: number, adBreaks: AdBreak[]): number => {
    const filteredAdBreaks = getAdBreaksBetween(0, position, adBreaks);

    if (this.autoSeekMode !== PlaybackAutoSeekMode.AUTO_SEEK_PLAY_LAST && !filteredAdBreaks.length) {
      return position;
    }

    const midrollAdBreaks = filteredAdBreaks.filter(
      (adBreak: AdBreak) => adBreak.position === AdContentTimePosition.MIDROLL,
    );

    switch (this.autoSeekMode) {
      case PlaybackAutoSeekMode.AUTO_SEEK_SKIP_ALL: {
        return position;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL: {
        const startPosition: number | undefined = this.playbackStartPositionForPreroll(filteredAdBreaks);

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_MIDROLL: {
        const startPosition: number | undefined = this.playbackStartPositionForMidroll(
          this.fastForwardMode,
          midrollAdBreaks,
        );

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_ALL: {
        const startPosition: number | undefined = this.playbackStartPositionForPlayAll(
          this.fastForwardMode,
          adBreaks,
          midrollAdBreaks,
        );

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
      case PlaybackAutoSeekMode.AUTO_SEEK_PLAY_LAST: {
        const previousCuePoint = this.streamManager.previousCuePointForStreamTime(position);
        let startPosition: number | undefined = undefined;
        this.logger.log('[DAI][POLICY] AUTO_SEEK_PLAY_LAST', position, previousCuePoint);
        if (previousCuePoint) {
          startPosition = previousCuePoint.start;
        }

        if (startPosition !== undefined) {
          this.autoSeekPositions.push(position);
          return startPosition;
        }

        break;
      }
    }

    return position;
  };

  /**
   * Checks if current playback time matches any watched ad break start times
   * and triggers ad break skipping based on playback repeat rules
   *
   * @param currentTime - Current playback time in seconds
   */
  handleWatchedAdBreak(currentTime: number): void {
    // Early return if no watched ad breaks
    if (!this.watchedAdBreaks?.length || currentTime == null) {
      return;
    }

    const normalizePlayHeadTime = this.normalizeTime(currentTime);
    // Using find instead of filter for better performance since we only need one match
    const matchingAdBreak = this.watchedAdBreaks.find(adBreak =>
      this.isTimeWithinAdBreak(normalizePlayHeadTime, adBreak),
    );

    // if we found a matching ad break that already been watched, and it shouldn't be repeated
    if (matchingAdBreak && !this.playbackRepeatedRule(matchingAdBreak)) {
      this.logger.log(`Skipping the already watched ad break`);
      this.skipWatchedAdBreak(matchingAdBreak, normalizePlayHeadTime);
    }
  }

  resolveSeekIntent = (position: number): number => {
    const nextSeekPosition: number | undefined = this.adBreakEnd();

    if (nextSeekPosition === undefined) {
      return position;
    }

    this.player.seek(nextSeekPosition);

    return nextSeekPosition;
  };

  getAutoSeekPositionIfAny = (): number | null => {
    return this.autoSeekPositions.shift() || null;
  };

  getContentPositionForPlayhead(playhead: number): number {
    //return this.streamManager.contentTimeForStreamTime(playhead);
    return playhead - getTotalAdvertsBreaksDuration(this.adBreaks ?? []);
  }

  getPlayheadForContentPosition(position: number): number {
    return this.streamManager.streamTimeForContentTime(position);
    // return position + getAdvertBreaksDurationPriorTo(position, this.adBreaks ?? []);
  }

  getContentDuration(): number {
    return this.player.duration - getTotalAdvertsBreaksDuration(this.adBreaks ?? []);
  }

  willSeekTo(position: number): number {
    if (
      this.player.isLive ||
      (this.autoSeekMode !== PlaybackAutoSeekMode.AUTO_SEEK_PLAY_LAST && (!this.adBreaks || !this.adBreaks.length))
    ) {
      // can never skip live promos
      //this.player.seek(position);

      return position;
    }

    if (this.player.currentTime > 0) {
      return this.applySeekPlaybackPolicy(position, this.adBreaks || []);
    } else {
      // if player is starting then resolve seek for initial playback position
      return this.resolveSeekIntentForInitialPlayback(position, this.adBreaks || []);
    }
  }

  getCurrentTime(_currentAdBreak: AdBreak | undefined, _ads: Ad[] | undefined): number {
    return 0;
  }

  getDuration(currentAdBreak: AdBreak | undefined, _ads: Ad[] | undefined): number {
    if (this.player.isLive) {
      return this.player.duration;
    }

    if (currentAdBreak) {
      return currentAdBreak.duration;
    } else if (!isNaN(this.player.duration)) {
      return this.player.duration - getTotalAdvertsBreaksDuration(this.adBreaks ?? []);
    }

    return this.player.duration;
  }

  discardAdBreak(currentAdBreak: AdBreak | undefined): void {
    if (!currentAdBreak) {
      return;
    }

    const skipToOffset = currentAdBreak.startTime + currentAdBreak.duration;

    this.player.seek(skipToOffset);
  }

  skipAd(currentAd: Ad | undefined, _ads: Ad[] | undefined): void {
    if (!currentAd) {
      return;
    }

    const skipToOffset = (currentAd.start + currentAd.duration) / 1000;

    this.player.seek(skipToOffset);
  }

  reset(): void {
    this.adBreaks = null;
    this.watchedAdBreaks = [];
    this.autoSeekPositions = [];
    this.policyInPlace = ActivePolicy.IDLE;
    this.logger.log('[DAI POLICY] Reset ad playback policy handler');
  }

  private playbackStartPositionForPreroll = (adBreaks: AdBreak[]): number | undefined => {
    if (!adBreaks.length) {
      return undefined;
    }

    const firstAdBreak = adBreaks[0];

    if (firstAdBreak.position === AdContentTimePosition.PREROLL) {
      return firstAdBreak.startTime;
    }

    return undefined;
  };

  private playbackStartPositionForMidroll = (
    _fastForwardMode: PlaybackFastForwardMode,
    midrollAdBreaks: AdBreak[],
  ): number | undefined => {
    if (midrollAdBreaks.length) {
      return this.playbackForwardPolicy(midrollAdBreaks);
    }

    return undefined;
  };

  private playbackStartPositionForPlayAll = (
    fastForwardMode: PlaybackFastForwardMode,
    adBreaks: AdBreak[],
    midrollAdBreaks: AdBreak[],
  ): number | undefined => {
    const prerollAdBreak = adBreaks[0];

    if (prerollAdBreak && prerollAdBreak.position === AdContentTimePosition.PREROLL) {
      const autoSeekPosition = this.playbackForwardPolicy(midrollAdBreaks);
      if (autoSeekPosition) {
        this.autoSeekPositions.push(autoSeekPosition);
      }
      return prerollAdBreak.startTime;
    } else {
      const startPosition: number | undefined = this.playbackStartPositionForMidroll(fastForwardMode, midrollAdBreaks);

      if (startPosition !== undefined) {
        return startPosition;
      }
    }

    return undefined;
  };

  private adBreakEnd = (): number | undefined => {
    if (!this.autoSeekPositions.length) {
      return undefined;
    }

    const nextPlayhead: number | undefined = this.autoSeekPositions[0];

    if (nextPlayhead) {
      this.updateAutoSeekOnAdBreakEnd();
      return nextPlayhead;
    }

    return undefined;
  };

  private updateAutoSeekOnAdBreakEnd = (): void => {
    this.autoSeekPositions.shift();
    this.updatePolicyStatusOnInterruptionEnd();
  };

  private updatePolicyStatusOnInterruptionEnd = (): void => {
    if (!this.autoSeekPositions.length) {
      this.setIdle();
    } else {
      this.setAutoSeek();
    }

    this.logger.log(`[PROMO POLICY] ad policy in place: ${this.policyInPlace}`);
  };

  private setIdle = (): void => {
    if (this.autoSeekPositions.length) {
      this.policyInPlace = ActivePolicy.IDLE;
    }
  };

  private setAutoSeek = (): void => {
    if (this.autoSeekPositions.length) {
      this.policyInPlace = ActivePolicy.AUTO_SEEK;
    }
  };

  /**
   * Checks if a given time falls within an ad break's range
   * @param currentTime player current time
   * @param adBreak Ad break to check against
   * @returns boolean indicating if time is within ad break
   */
  private isTimeWithinAdBreak(currentTime: number, adBreak: AdBreak): boolean {
    const adBreakStart = this.normalizeTime(adBreak.startTime);
    const quarterDuration = this.normalizeTime(adBreak.duration / 4);

    // Check if playhead falls within first quarter (25%) of ad break duration
    // Example: For ad break at 568.6s with duration 30.096s:
    //   - Quarter duration = 30.096s / 4 = 7.524s
    //   - Valid range: 568.6s to 576.124s (start time + 7.524s)
    // This ensures we only detect and skip ads near their beginning,
    // avoiding skips when playhead is too far into the ad break
    const timeDifference = currentTime - adBreakStart;
    this.logger.log('Ad Break Time Comparison', timeDifference);
    return timeDifference >= 0 && timeDifference <= quarterDuration;
  }

  private normalizeTime(time: number): number {
    // Early return
    if (time < 0) {
      return 0;
    }
    return Number(time.toFixed(DECIMAL_PLACES));
  }

  /**
   * skipWatchedAdBreak method with safety checks
   * @param currentTime player current time, normalized
   * @param adBreak Ad break to check against
   * @return void
   */
  private skipWatchedAdBreak(adBreak: AdBreak, currentTime: number): void {
    if (!adBreak) {
      return;
    }

    const targetPosition = this.normalizeTime(adBreak.startTime + adBreak.duration);

    // Only seek if we're actually moving forward
    if (targetPosition > currentTime) {
      this.player.seek(targetPosition);
      this.logger.log(`Skipped the Watched Ad Break: ${targetPosition}`);
    }
  }
}
